import {
  Configuration,
  DataItem,
  Dimensions,
  Gutter,
  OptionsMaterial,
  Params,
  Roof,
  Wall,
} from "src/types/configuration";
import ConfiguratorService from "src/services/configuratorService";
import { loadPrices, getPricesSync, PriceItem } from "src/helpers/priceLoader";
import { calculateKapschuurRoofBigSideLength } from "src/helpers/configuration";
import {
  CANOPY_ROW_NAME,
  GLASPLAAT_DAK_ARTICLES,
  STAANDER_MEERPRIJS,
  formatCanopySizeInCm,
  getMontageAmount,
  getStaanderLengthInMeters,
  getStaanderMeerprijsQuantity,
} from "src/helpers/calculation";
import {
  findVelvetlineRow,
  getVelvetlineExtras,
  getVelvetlinePrice,
  isTwoPostsChosen,
  resolveVelvetlineMaterial,
  resolveVelvetlineSpan,
  VelvetlineExtraMaterial,
} from "src/data/velvetlinePrices";

class CalculationService {
  private static instance: CalculationService;
  private configuratorService?: ConfiguratorService;
  private state?: Configuration;
  private dimensions?: Dimensions;
  private roof?: Roof;
  private roofOverhang = 0;
  private gutter?: Gutter;
  private walls?: Wall[];
  private data: DataItem[] = [];
  private optionsMaterial: OptionsMaterial | undefined;
  private params: Params | undefined;
  private prices: PriceItem[] = [];

  constructor(
    initConfiguration: Configuration,
    configuratorService: ConfiguratorService
  ) {
    if (CalculationService.instance) {
      return CalculationService.instance;
    }

    CalculationService.instance = this;
    this.state = initConfiguration;
    this.configuratorService = configuratorService;
    this.dimensions = initConfiguration.dimensions;
    this.roof = initConfiguration.roof;
    this.gutter = initConfiguration.gutter;
    this.walls = initConfiguration.walls;
    this.optionsMaterial = initConfiguration.optionsMaterial;
    this.params = initConfiguration.params;

    this.prices = getPricesSync();
    this.initializeData();

    this.loadPricesAsync();
  }

  private async loadPricesAsync(): Promise<void> {
    try {
      this.prices = await loadPrices();
      this.initializeData();
    } catch (error) {
      console.error("Failed to load prices asynchronously:", error);
    }
  }

  private initializeData(): void {
    this.data = this.prices
      .filter((price) => price && price.price > 0)
      .map((p) => {
        return {
          name: p.name,
          label: p.label,
          unit: p.unit,
          price: p.price,
          quantity: 0,
          total: 0,
        };
      });
  }

  setState(state: Configuration): void {
    this.state = state;
    this.dimensions = state.dimensions;
    this.roof = state.roof;
    this.gutter = state.gutter;
    this.walls = state.walls;
    this.optionsMaterial = state.optionsMaterial;
    this.params = state.params;
  }

  getData() {
    return this.data.filter((item) => {
      if (!item) return false;

      // @ts-ignore
      if (this.state.roofType === "platdak") {
        if (item.name === "dakpannen" || item.name === "dakpannen_terracota") {
          return false;
        }
      }

      return true;
    });
  }

  getEPDMGlue() {
    const item = this.getPriceItem("epdmGlue");
    return item ? item.price * this.calculatePlatdakRoofSquareInSqMeters() : 0;
  }

  getBlackTrim() {
    const item = this.getPriceItem("daktrim_zwart");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getAluminiumTrim() {
    const item = this.getPriceItem("daktrim_blank");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getPoer(name: string) {
    const item = this.getPriceItem(name);
    if (!item) return 0;
    const quantity = window.CONF.house.columns.length / 2;
    return item.price * quantity;
  }

  getLight(name: string) {
    if (name === "all" || name === "some") {
      const item = this.getPriceItem("spotlight");
      if (!item) return 0;
      const oase = this.state?.roofType === "oase";
      const houseWidth = (this.dimensions?.width ?? 0) / 1000;
      const glassCheck = this.state?.roofGlassMat.glass === "glass";
      const everyLigger = name === "all";

      const amount =
        this.getTablePanelCount(
          houseWidth,
          glassCheck,
          oase ? "oase" : "aludra"
        ) - 1;

      const quantity = everyLigger ? amount * 2 : amount - (amount % 2);
      return quantity * item.price;
    } else return 0;
  }

  getSpie(left = false, right = false) {
    if (!left && !right) return 0;
    let spyquantity = 0;
    if (left) spyquantity++;
    if (right) spyquantity++;

    const frame = this.getPriceItem("spyFrame");
    const polycarbonatSpy =
      this.getPriceItem("polycarbonaatSpie") ??
      this.getPriceItem("polycarbonatSpy");
    const polycarbonatWideSpy = this.getPriceItem("polycarbonatWideSpy");
    const glassSpy = this.getPriceItem("glassSpy");
    const glassSpy44 = this.getPriceItem("glassSpy44") ?? glassSpy;
    if (!frame) return 0;

    const glassShade = this.state?.roofGlassMat.glassshade;
    const glassCheck = this.state?.roofGlassMat.glass === "glass";
    const glassDarkCheck = glassShade === "dark";
    const glassMilkCheck = glassShade === "melk";
    const glassLightCheck =
      glassShade === "light" || (!glassDarkCheck && !glassMilkCheck);

    const polyWide =
      this.state?.roofGlassMat.glass === "polywide" ||
      (this.state?.roofGlassMat.glass === "poly" &&
        this.state?.roofGlassMat.polyshade === "wide");
    const poly = this.state?.roofGlassMat.glass === "poly";
    const { area: spiearea, perimeter: spieperimeter } = this.getSpieGeometry();

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;

    let quantity = 0;
    let total = 0;

    total += spyquantity * roundUpToNearestHalf(spieperimeter) * frame.price;
    quantity = poly ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
    if (polycarbonatSpy) total += quantity * polycarbonatSpy.price;
    quantity = polyWide ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
    if (polycarbonatWideSpy) total += quantity * polycarbonatWideSpy.price;
    quantity = spyquantity * roundUpToNearestHalf(spiearea);
    if (glassSpy) total += quantity * glassSpy.price;
    quantity = spyquantity * roundUpToNearestHalf(spiearea);
    if (glassSpy44) total += quantity * glassSpy44.price;

    return total;
  }

  getSlidingWallPanels(type: string) {
    switch (type) {
      case "window_2x2Tracks":
        return 2;
      case "door_3x3Tracks":
        return 3;
      case "window_4x4Tracks":
      case "door_4x4Tracks":
      case "door_4x2Tracks":
        return 4;
      case "door_5x3Tracks":
      case "door_5x5Tracks":
        return 5;
      case "door_6x6_Tracks":
      case "door_6x3_Tracks":
        return 6;
      case "door_7x7_Tracks":
      case "door_7x4_Tracks":
        return 7;
      default:
        return 0;
    }
  }

  getSlidingDoorsPrice(length: number, type: string = "light") {
    let amount = 0;

    if (length >= 6250) {
      amount = 7;
    } else if (length >= 5250) {
      amount = 6;
    } else if (length >= 4250) {
      amount = 5;
    } else if (length >= 3250) {
      amount = 4;
    } else if (length >= 2240) {
      amount = 3;
    } else {
      amount = 3;
    }

    const base = this.getPriceItem("glassdoors")?.price ?? 0;
    const darkSurcharge =
      type === "dark" ? this.getPriceItem("glassdoorsdark")?.price ?? 0 : 0;

    return amount * (base + darkSurcharge);
  }

  getWindowsPrice(lenght: number, type: string = "light") {
    const amount = lenght > 2500 ? 4 : 2;

    const base = this.getPriceItem("glassdoors")?.price ?? 0;
    const steellookSurcharge = this.getPriceItem("windowdoors")?.price ?? 0;
    const darkSurcharge =
      type === "dark" ? this.getPriceItem("windowdoorsdark")?.price ?? 0 : 0;

    return amount * (base + steellookSurcharge + darkSurcharge);
  }

  GetAlum(length: number, isLeft: boolean = false, isRight: boolean = false) {
    const item = this.getPriceItem("aluminiumWall");
    if (!item) return 0;

    return item.price * this.getAluminiumWallLength(length / 1000);
  }

  getFundering(name: string) {
    const item = this.getPriceItem(name);
    if (!item) return 0;
    let quantity = 0;
    let total = 0;
    const truewalls = window.CONF.house.walls.map((item) => {
      return [
        item.sections[0].type,
        item.sections[0].length / 1000,
        item.sections[0].customization.includes("glassSectionDark"),
      ];
    });

    truewalls.map((item) => {
      if (item[0].startsWith("door") || item[0].startsWith("window"))
        quantity += this.getFunderingStockLength(item[1]);
    });
    total = item.price * quantity;
    return total;
  }

  getMontagePercent() {
    return this.getPriceItem("montage")?.price ?? 0;
  }

  getOpslagGlasplaatDakPercent() {
    return this.getPriceItem("opslagGlasplaatDak")?.price ?? 0;
  }

  getGlasplaatDakSubtotal() {
    return this.data.reduce((acc, row) => {
      if (!row || !GLASPLAAT_DAK_ARTICLES.includes(row.name)) return acc;
      return acc + row.total;
    }, 0);
  }

  getOpslagGlasplaatDakTotal() {
    return (
      this.getGlasplaatDakSubtotal() * (this.getOpslagGlasplaatDakPercent() / 100)
    );
  }

  priceWith(patch: Partial<Configuration>): number {
    if (!this.state || !window.CONF) return 0;

    const savedState = this.state;
    const savedData = this.data;

    try {
      this.setState({ ...savedState, ...patch });
      this.calculateConfiguration();
      return this.calculateTotalPrice();
    } finally {
      this.setState(savedState);
      this.data = savedData;
    }
  }

  getSurcharge(
    option: Partial<Configuration>,
    groupDefault: Partial<Configuration>
  ): number {
    return this.priceWith(option) - this.priceWith(groupDefault);
  }

  applyMontage(price: number): number {
    if (!this.state?.optionsMaterial.montage) return price;

    return price * (1 + this.getMontagePercent() / 100);
  }

  private getSubtotalExcludingMontage() {
    return this.data.reduce((acc, row) => {
      if (!row || row.name === "montage") return acc;
      return acc + row.total;
    }, 0);
  }

  getMontageTotal() {
    return getMontageAmount(
      this.getSubtotalExcludingMontage(),
      this.getMontagePercent()
    );
  }

  getPoertegel() {
    const item = this.getPriceItem("betontegel");
    if (!item) return 0;
    if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity =
      this.optionsMaterial.poerQuantity > 0 &&
        this.optionsMaterial.poer === "poertegel"
        ? this.optionsMaterial.poerQuantity
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsWit() {
    const item = this.getPriceItem("liter_beits_wit");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_wit) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_wit > 0
        ? this.optionsMaterial.liter_beits_wit
        : 1;
    return item ? item.price * quantity : 0;
  }

  getLiterBeitsZwart() {
    const item = this.getPriceItem("liter_beits_zwart");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_zwart) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_zwart > 0
        ? this.optionsMaterial.liter_beits_zwart
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsGray() {
    const item = this.getPriceItem("liter_beits_gray");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_gray) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_gray > 0
        ? this.optionsMaterial.liter_beits_gray
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsDouglas() {
    const item = this.getPriceItem("liter_beits_douglas");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_douglas) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_douglas > 0
        ? this.optionsMaterial.liter_beits_douglas
        : 1;
    return item.price * quantity;
  }

  getRoofingPrice(name: string) {
    if (!this.data || !this.state) return 0;

    const kapschuurDepthRoofSide =
      this.state.params.kapschuurOverhangLength +
      calculateKapschuurRoofBigSideLength(this.state);
    const width = this.dimensions?.width ?? 0;
    const quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
    let price = 0;

    if (name === "dakpanplaat") {
      price = this.getPriceItem("dakpanprofielen-zwart")?.price ?? 0;
    }

    if (name === "dakpanplaat_rood") {
      price = this.getPriceItem("dakpanprofielen-terracotta")?.price ?? 0;
    }

    if (name === "leiendak") {
      price = this.getPriceItem("shingles-zwart")?.price ?? 0;
    }

    if (name === "leiendak_red") {
      price = this.getPriceItem("shingles-rood")?.price ?? 0;
    }

    if (name === "leiendak_green") {
      price = this.getPriceItem("shingles-groen")?.price ?? 0;
    }

    return price * quantity;
  }

  getGutter(name: string): number {
    const houseWidth = this.state!.dimensions.width / 1000;
    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;
    const profileLength = this.getWidthProfileStockLength(houseWidth);

    if (this.state?.roofType === "aludra") {
      const bodyName = name === "default" ? "goot" : "gootsierlijstCube";
      const gutterBody = this.getPriceItem(bodyName);

      return gutterBody
        ? gutterBody.price * roundUpToNearestHalf(houseWidth)
        : 0;
    }

    const sierlijstName =
      name === "rond"
        ? "gootsierlijstRond"
        : name === "klassiek"
          ? "gootsierlijstKlassiek"
          : undefined;
    if (!sierlijstName) return 0;

    const gutterBody = this.getPriceItem(sierlijstName);

    return gutterBody ? gutterBody.price * profileLength : 0;
  }

  getGlass(name: string): number {
    const currentRoofType = this.state?.roofType;
    if (!currentRoofType) return 0;

    const depth = this.dimensions?.depth ?? 0;
    const width = this.dimensions?.width ?? 0;
    const houseDepth = depth / 1000;
    const houseWidth = width / 1000;

    const roundUpToNearestHalf = (num: number): number =>
      Math.ceil(num * 2) / 2;
    const roundUpToNearestTenth = (num: number): number =>
      Math.ceil(num * 10) / 10;
    const roundUpToCm = (num: number): number => Math.ceil(num * 100) / 100;

    const isGlassItem =
      name === "glass" || name === "glassdark" || name === "glassMelk";
    const panelCount = this.getTablePanelCount(
      houseWidth,
      isGlassItem,
      currentRoofType
    );
    const panelLength =
      currentRoofType === "oase"
        ? roundUpToNearestHalf(
          (houseDepth - 0.11) / Math.cos((8 * Math.PI) / 180)
        )
        : roundUpToNearestHalf(houseDepth);
    const panelWidth = roundUpToCm(houseWidth / panelCount);
    const quantity = panelCount * roundUpToNearestTenth(panelLength * panelWidth);

    const item = this.getPriceItem(name);
    if (!item) return 0;

    if (name === "glass") {
      return houseDepth <= 3.5 ? quantity * item.price : 0;
    }
    if (name === "glassdark") {
      return houseDepth > 3.5 ? quantity * item.price : 0;
    }
    return quantity * item.price;
  }

  roundValue(num: number, precision: number = 2) {
    return parseFloat(num.toFixed(precision));
  }

  formatToCurrency(price: number) {
    return price.toLocaleString("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  getCanopyPrice(): number {
    if (this.state?.roofType !== "oase") return 0;

    const material = resolveVelvetlineMaterial(this.state.roofGlassMat?.glass);
    const width = this.dimensions?.width ?? 0;
    const depth = this.dimensions?.depth ?? 0;
    const twoPosts = isTwoPostsChosen(this.dimensions?.frontlimit ?? 0);

    return getVelvetlinePrice(material, width, depth, twoPosts);
  }

  private getCanopyRow(): DataItem | undefined {
    if (this.state?.roofType !== "oase") return undefined;

    const material = resolveVelvetlineMaterial(this.state.roofGlassMat?.glass);
    const row = findVelvetlineRow(
      material,
      this.dimensions?.width ?? 0,
      this.dimensions?.depth ?? 0
    );
    if (!row) return undefined;

    const price = this.getCanopyPrice();
    const materialLabel = material === "glas" ? "glas" : "polycarbonaat";

    return {
      name: CANOPY_ROW_NAME,
      label: `Overkapping Velvetline ${materialLabel} ${
        row.article
      } (${formatCanopySizeInCm(row.width, row.depth)})`,
      unit: "stuk",
      price,
      quantity: 1,
      total: price,
    };
  }

  getCanopyExtras(): VelvetlineExtraMaterial[] {
    if (this.state?.roofType !== "oase") return [];

    return getVelvetlineExtras(
      resolveVelvetlineMaterial(this.state.roofGlassMat?.glass),
      this.dimensions?.width ?? 0,
      this.dimensions?.depth ?? 0,
      isTwoPostsChosen(this.dimensions?.frontlimit ?? 0)
    );
  }

  calculateTotalPrice(): number {
    const articleTotal = this.data.reduce((acc, item) => {
      if (!item || item.quantity === 0) return acc;
      if (item.name === CANOPY_ROW_NAME) return acc;
      return acc + item.total;
    }, 0);

    return this.getCanopyPrice() + articleTotal;
  }

  getFormatTotalPrice(): string {
    return this.formatToCurrency(this.calculateTotalPrice());
  }

  toMeter = (value: number): number => {
    const meters = value / 1000;

    return parseFloat(meters.toFixed(2));
  };

  toSqMeter(value: number): number {
    const meters = value / 1_000_000;

    return parseFloat(meters.toFixed(2));
  }

  calculateRoofSideLength() {
    const base = this.dimensions?.depth ?? 0;
    const angle = this.dimensions?.angle ?? 8;
    const angleInRadians = (angle * Math.PI) / 180;
    const sideLength =
      (base + this.roofOverhang) / (2 * Math.cos(angleInRadians));

    return parseFloat(sideLength.toFixed(2));
  }

  calculateRoofSquareInSqMeters() {
    const width = this.dimensions?.width ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(2 * sideLength * (width + this.roofOverhang));
  }

  calculatePlatdakRoofSquareInSqMeters() {
    const width = this.getWidthWithOverhang();
    const depth = this.getDepthWithOverhang();
    return this.toSqMeter(width * depth);
  }

  calculateTriangleAreaInSqMeters() {
    const base = this.dimensions?.depth ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(
      (1 / 2) * base * Math.sqrt(4 * sideLength ** 2 - base ** 2)
    );
  }

  getCountVerticalBalks() {
    return this.configuratorService?.getColumnsBalks() ?? 0;
  }

  getStaanderCount(): number {
    if (this.state?.roofType !== "oase") return this.getCountVerticalBalks() / 2;

    return resolveVelvetlineSpan(
      this.dimensions?.width ?? 0,
      this.dimensions?.depth ?? 0,
      resolveVelvetlineMaterial(this.state.roofGlassMat?.glass),
      isTwoPostsChosen(this.dimensions?.frontlimit ?? 0)
    ).staanders;
  }

  getWidthWithOverhang() {
    const width = this.dimensions?.width ?? 0;
    const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    const overhangRight = this.dimensions?.overhangRight ?? 0;
    return width + overhangLeft + overhangRight;
  }

  getDepthWithOverhang() {
    const depth = this.dimensions?.depth ?? 0;
    const overhangBack = this.dimensions?.overhangBack ?? 0;
    const overhangFront = this.dimensions?.overhangFront ?? 0;
    return depth + overhangBack + overhangFront;
  }

  getRoofPerimeterInMeters() {
    const width = this.getWidthWithOverhang() * 2;
    const depth = this.getDepthWithOverhang() * 2;
    return this.toMeter(width + depth);
  }

  getPriceItemFromData(key: string) {
    return this.data.find((item) => item && item.name === key);
  }

  getPriceItem(key: string) {
    const item = this.prices.find((item) => item && item.name === key);

    return item ? { ...item } : undefined;
  }

  private getSpieGeometry() {
    const houseDepth = (this.dimensions?.depth ?? 0) / 1000;
    const effectiveDepth = Math.max(houseDepth, 3);
    const base = effectiveDepth - 0.11;

    const area = Math.tan((8 * Math.PI) / 180) * base * base;
    const perimeter =
      base +
      base * Math.tan(8 * (Math.PI / 180)) +
      base / Math.cos(8 * (Math.PI / 180));

    return { area, perimeter };
  }

  private getRoundedStaanderLength(clearHeightInMeters: number): number {
    return getStaanderLengthInMeters(clearHeightInMeters);
  }

  private roundUpToAvailableLength(
    requiredLength: number,
    availableLengths: number[],
    minimumLength = 0
  ): number {
    const targetLength = Math.max(requiredLength, minimumLength);
    for (const length of availableLengths) {
      if (targetLength <= length) return length;
    }

    return availableLengths[availableLengths.length - 1];
  }

  private getClearRoofWidthInCm(
    widthInMeters: number,
    roofType: string
  ): number {
    const insetInCm = roofType === "oase" ? 5.8 : 35.8;
    return Math.max(0, widthInMeters * 100 - insetInCm);
  }

  private getTablePanelCount(
    widthInMeters: number,
    isGlassRoof: boolean,
    roofType: string
  ): number {
    const clearWidthInCm = this.getClearRoofWidthInCm(widthInMeters, roofType);
    const panelWidthInCm = isGlassRoof ? 75 : 100;
    return Math.max(1, Math.ceil(clearWidthInCm / panelWidthInCm));
  }

  private getTableLiggerCount(
    widthInMeters: number,
    isGlassRoof: boolean,
    roofType: string
  ): number {
    return Math.max(
      3,
      this.getTablePanelCount(widthInMeters, isGlassRoof, roofType) + 1
    );
  }

  private getWidthProfileStockLength(widthInMeters: number): number {
    const requiredLength = widthInMeters <= 2.5 ? 2.55 : widthInMeters;
    return this.roundUpToAvailableLength(requiredLength, [5.09, 6.15, 7.09, 8.15], 5.09);
  }

  private getLiggerStockLength(depthInMeters: number): number {
    const requiredLength = depthInMeters <= 2 ? 2 : depthInMeters;
    return this.roundUpToAvailableLength(requiredLength, [4, 4.97, 5.97, 6.97], 4);
  }

  private getAfdekkerStockLength(depthInMeters: number): number {
    const requiredLength = depthInMeters <= 2 ? 2.02 : depthInMeters;
    return this.roundUpToAvailableLength(requiredLength, [4.03, 5.03, 6.03, 7.03], 4.03);
  }

  private getKliklijstStockLength(
    widthInMeters: number,
    liggerCount: number,
    liggerWidthInMeters: number
  ): number {
    const requiredLength =
      widthInMeters <= 2.5
        ? 2.55
        : Math.max(widthInMeters - liggerCount * liggerWidthInMeters, 0);
    return this.roundUpToAvailableLength(requiredLength, [4.78], 4.78);
  }

  private getAluminiumWallLength(lengthInMeters: number): number {
    return Math.max(Math.ceil(lengthInMeters * 2) / 2, 3);
  }

  private getFunderingStockLength(sectionLengthInMeters: number): number {
    return this.roundUpToAvailableLength(
      sectionLengthInMeters,
      [2.5, 3, 3.5, 5, 6],
      2.5
    );
  }

  private isWithinStandardPolyWidth(widthInMeters: number): boolean {
    return (
      (widthInMeters >= 4 && widthInMeters < 4.06) ||
      (widthInMeters >= 5 && widthInMeters < 5.06) ||
      (widthInMeters >= 6 && widthInMeters < 6.06) ||
      (widthInMeters >= 7 && widthInMeters < 7.06)
    );
  }

  private calculateSunScreenFormula(widthInMeters: number, depthInMeters: number) {
    const orderedDepth = Math.ceil((depthInMeters + 0.15) * 2) / 2;

    return (
      3201 +
      90 * widthInMeters +
      153 * orderedDepth +
      34.5 * widthInMeters * orderedDepth +
      (orderedDepth >= 4 ? 303 : 0)
    );
  }

  async refreshPrices(): Promise<void> {
    await this.loadPricesAsync();
  }

  getCurrentPrices(): PriceItem[] {
    return this.prices;
  }

  calculateConfiguration(): DataItem[] {
    if (!this.data || !this.state) return [];

    const columns = window.CONF.house.columns.length / 2;
    const width = this.dimensions?.width ?? 0;
    const depth = this.dimensions?.depth ?? 0;
    const height = this.dimensions?.height ?? 0;

    const truewalls = window.CONF.house.walls.map((item) => {
      return [
        item.sections[0].type,
        item.sections[0].length / 1000,
        item.sections[0].customization.includes("glassSectionDark"),
      ];
    });

    const roundUpToNearestHalf = (number: number) => {
      return Math.ceil(number * 2) / 2;
    };

    const roundUpToCm = (number: number) => {
      return Math.ceil(number * 100) / 100;
    };

    const roundUpToNearestTenth = (number: number) => {
      return Math.ceil(number * 10) / 10;
    };

    const hasPolycarbonaatSpie = this.prices.some(
      (price) => price.name === "polycarbonaatSpie"
    );

    this.data = this.prices.map((priceItem) => {
      if (!priceItem) return priceItem;

      const item = { ...priceItem };

      const oase = this.state?.roofType === "oase";
      const aludra = this.state?.roofType === "aludra";
      const sunscreen = this.state?.sunScreen.enabled;
      const lights = this.state?.lights.enabled;

      const roofGlass = this.state?.roofGlassMat.glass;
      const glassShade = this.state?.roofGlassMat.glassshade;
      const glassCheck = roofGlass === "glass";
      const glassDarkCheck = glassShade === "dark";
      const glassMilkCheck = glassShade === "melk";
      const glassLightCheck =
        glassShade === "light" || (!glassDarkCheck && !glassMilkCheck);
      const polyWide =
        roofGlass === "polywide" ||
        (roofGlass === "poly" && this.state?.roofGlassMat.polyshade === "wide");
      const poly = roofGlass === "poly";
      const isPolyRoof = poly || polyWide;
      const polylightCheck = this.state?.roofGlassMat.polyshade === "light";
      const polymidCheck = this.state?.roofGlassMat.polyshade === "mid";
      const polydarkCheck = this.state?.roofGlassMat.polyshade === "dark";

      const poer = this.state?.optionsMaterial.poer === "poer";
      const hwapoer = this.state?.optionsMaterial.poer === "hwapoer";

      const houseWidth = width / 1000;
      const houseDepth = depth / 1000;
      const houseHeight = height / 1000;
      const isGlassRoof = roofGlass === "glass";
      const tableLiggerCount = this.getTableLiggerCount(
        houseWidth,
        isGlassRoof,
        oase ? "oase" : "aludra"
      );
      const glassDepthUses44 = houseDepth <= 3.5;

      const staanderLength = this.getRoundedStaanderLength(houseHeight);
      const liggerWidth = 0.058;
      const houseColumnSize = oase ? 0.11 : 0.15;
      const widthProfileStockLength = this.getWidthProfileStockLength(houseWidth);
      const liggerStockLength = this.getLiggerStockLength(houseDepth);
      const afdekkerStockLength = this.getAfdekkerStockLength(houseDepth);
      const kliklijstStockLength = this.getKliklijstStockLength(
        houseWidth,
        tableLiggerCount,
        liggerWidth
      );
      const { area: spiearea, perimeter: spieperimeter } =
        this.getSpieGeometry();

      let quantity = 0;
      let total = 0;

      if (item.name === "montageset") {
        quantity = 1;
        total = quantity * item.price;
      }
      if (item.name === "pvcBuis") {
        quantity = 1;
        total = quantity * item.price;
      }
      if (item.name === "pvcBocht90gr") {
        quantity = 2;
        total = quantity * item.price;
      }
      if (item.name === "toeslagProductie") {
        quantity = 1;
        total = quantity * item.price;
      }

      const liggerLength =
        (houseDepth - houseColumnSize) / Math.cos((8 * Math.PI) / 180);

      const ligtoHalfLength = roundUpToNearestHalf(liggerLength);

      if (aludra) {
        const amount = tableLiggerCount;
        const liggerCount = tableLiggerCount;
        const defaultgoot = this.state?.gutter.type === "default";

        if (item.name === "staander_150x150") {
          quantity =
            (this.getCountVerticalBalks() / 2) *
            roundUpToNearestHalf(houseHeight);
          total = quantity * item.price;
        }
        if (item.name === "goot") {
          quantity = defaultgoot ? 1 * roundUpToNearestHalf(houseWidth) : 0;
          total = quantity * item.price;
        }
        if (item.name === "gootsierlijstCube") {
          quantity = !defaultgoot ? 1 * roundUpToNearestHalf(houseWidth) : 0;
          total = quantity * item.price;
        }
        if (item.name === "afdekkapGootprofiel") {
          quantity = 2;
          total = quantity * item.price;
        }
        if (item.name === "ligger_165x200") {
          quantity = 2 * roundUpToNearestHalf(houseDepth);
          total = quantity * item.price;
        }
        if (item.name === "ligger") {
          quantity = liggerCount * roundUpToNearestHalf(houseDepth);
          total = quantity * item.price;
        }
        if (item.name === "middenAfdekker") {
          quantity = (amount - 2) * roundUpToNearestHalf(houseDepth);
          total = quantity * item.price;
        }
        if (item.name === "zijAfdekker") {
          quantity = 2 * ligtoHalfLength;
          total = quantity * item.price;
        }
        if (item.name === "plaatstopper") {
          quantity = amount;
          total = quantity * item.price;
        }
        if (item.name === "muurplaat") {
          quantity = 1 * roundUpToNearestHalf(houseWidth);
          total = quantity * item.price;
        }
        if (item.name === "afdekkapmuurprofiel") {
          quantity = 2;
          total = quantity * item.price;
        }
        if (item.name === "condensprofiel") {
          quantity = isPolyRoof ? amount - 1 : 0;
          total = quantity * item.price;
        }
        if (item.name === "kliklijst") {
          quantity = 2 * kliklijstStockLength;
          total = quantity * item.price;
        }

        const glassAmount =
          (amount - 1) *
          roundUpToNearestTenth(
            houseDepth * roundUpToCm(houseWidth / (amount - 1))
          );
        const glassAmountMilk = glassAmount;
        const polyAmount = liggerCount - 1;
        const polyChargedAmount = glassAmount;

        if (item.name === "glass") {
          quantity =
            glassCheck && !glassMilkCheck && glassDepthUses44 ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "glassdark") {
          quantity =
            glassCheck && !glassMilkCheck && !glassDepthUses44 ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "glassMelk") {
          quantity = glassCheck && glassMilkCheck ? glassAmountMilk : 0;
          total = quantity * item.price;
        }
        if (item.name === "polylight") {
          quantity = poly && polylightCheck ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polymid") {
          quantity = poly && polymidCheck ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polydark") {
          quantity = poly && polydarkCheck ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polywide") {
          quantity = polyWide ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polyOpMaat") {
          quantity =
            isPolyRoof && !this.isWithinStandardPolyWidth(houseWidth)
              ? liggerCount - 1
              : 0;
          total = quantity * item.price;
        }
      }
      if (oase) {
        const rond = this.state?.gutter.type === "rond";
        const klassiek = this.state?.gutter.type === "klassiek";
        const defaultgoot = this.state?.gutter.type === "default";

        const spyleft = this.state?.spy.left;
        const spyright = this.state?.spy.right;

        const amount = tableLiggerCount;
        const liggerCount = tableLiggerCount;

        if (item.name === "standdeer_110x110") {
          quantity = this.getStaanderCount() * staanderLength;
          total = quantity * item.price;
        }
        if (item.name === "gootprofiel") {
          quantity = 1 * widthProfileStockLength;
          total = quantity * item.price;
        }
        if (item.name === "afdekkapGootprofiel") {
          quantity = defaultgoot ? 2 : 0;
          total = quantity * item.price;
        }
        if (item.name === "zijAfdekker") {
          quantity = 2 * afdekkerStockLength;
          total = quantity * item.price;
        }
        if (item.name === "middenAfdekkerVelvetline") {
          quantity = (amount - 2) * afdekkerStockLength;
          total = quantity * item.price;
        }
        if (item.name === "liggerVelvetline") {
          quantity = liggerCount * liggerStockLength;
          total = quantity * item.price;
        }
        if (item.name === "muurplaatVelvetline") {
          quantity = 1 * widthProfileStockLength;
          total = quantity * item.price;
        }
        if (item.name === "afdekkapmuurprofiel") {
          quantity = 2;
          total = quantity * item.price;
        }
        if (item.name === "condensprofielVelvetline") {
          quantity = isPolyRoof ? amount - 1 : 0;
          total = quantity * item.price;
        }
        if (item.name === "velvetlinePlaatstopper") {
          quantity = amount;
          total = quantity * item.price;
        }
        if (item.name === "kliklijst") {
          quantity = 2 * kliklijstStockLength;
          total = quantity * item.price;
        }
        if (item.name === "liggerVerzwaring") {
          quantity =
            glassCheck && houseDepth > 3.5 ? liggerCount * liggerStockLength : 0;
          total = quantity * item.price;
        }
        if (item.name === "gootsierlijstKlassiek") {
          quantity = klassiek ? 1 * widthProfileStockLength : 0;
          total = quantity * item.price;
        }
        if (item.name === "gootsierlijstRond") {
          quantity = rond ? 1 * widthProfileStockLength : 0;
          total = quantity * item.price;
        }

        const glassAmount =
          (amount - 1) *
          roundUpToNearestTenth(
            ligtoHalfLength * roundUpToCm(houseWidth / (amount - 1))
          );
        const glassAmountMilk = glassAmount;
        const polyAmount = liggerCount - 1;
        const polyChargedAmount = glassAmount;

        if (item.name === "glass") {
          quantity =
            glassCheck && !glassMilkCheck && glassDepthUses44 ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "glassdark") {
          quantity =
            glassCheck && !glassMilkCheck && !glassDepthUses44 ? glassAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "glassMelk") {
          quantity = glassCheck && glassMilkCheck ? glassAmountMilk : 0;
          total = quantity * item.price;
        }
        if (item.name === "polylight") {
          quantity = poly && polylightCheck ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polymid") {
          quantity = poly && polymidCheck ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polydark") {
          quantity = poly && polydarkCheck ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polywide") {
          quantity = polyWide ? polyChargedAmount : 0;
          total = quantity * item.price;
        }
        if (item.name === "polyOpMaat") {
          quantity =
            isPolyRoof && !this.isWithinStandardPolyWidth(houseWidth)
              ? liggerCount - 1
              : 0;
          total = quantity * item.price;
        }

        if (spyleft || spyright) {
          let spyquantity = 0;
          if (spyleft) spyquantity++;
          if (spyright) spyquantity++;

          if (item.name === "spyFrame") {
            quantity = spyquantity * roundUpToNearestHalf(spieperimeter);
            total = quantity * item.price;
          }
          if (item.name === "polycarbonatSpy") {
            quantity =
              !hasPolycarbonaatSpie && poly
                ? spyquantity * roundUpToNearestHalf(spiearea)
                : 0;
            total = quantity * item.price;
          }
          if (item.name === "polycarbonaatSpie") {
            quantity = poly ? spyquantity * roundUpToNearestHalf(spiearea) : 0;
            total = quantity * item.price;
          }
          if (item.name === "polycarbonatWideSpy") {
            quantity = polyWide
              ? spyquantity * roundUpToNearestHalf(spiearea)
              : 0;
            total = quantity * item.price;
          }
          if (item.name === "glassSpy") {
            quantity = glassCheck && glassDarkCheck
              ? spyquantity * roundUpToNearestHalf(spiearea)
              : 0;
            total = quantity * item.price;
          }
          if (item.name === "glassSpy44") {
            quantity = glassCheck && (glassLightCheck || glassMilkCheck)
              ? spyquantity * roundUpToNearestHalf(spiearea)
              : 0;
            total = quantity * item.price;
          }
        }
      }
      if (sunscreen) {
        if (item.name === "sunScreenBody") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenSideGuide") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenGuideMountingBracketsBottom") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenGuideMountingBracketsTop") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenGuideMountingBracketsTopVelvetline") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenTextile") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenMolding") {
          quantity = 0;
          total = 0;
        }
        if (item.name === "sunScreenBovendakOnderdak") {
          quantity = 1;
          item.price = this.calculateSunScreenFormula(houseWidth, houseDepth);
          total = quantity * item.price;
        }
      }
      if (true) {
        if (item.name === "glassdoors") {
          truewalls.map((wall) => {
            quantity += this.getSlidingWallPanels(wall[0] as string);
          });
          total = item.price * quantity;
        }
        if (item.name === "glassdoorsdark") {
          truewalls.map((wall) => {
            if (wall[2] && (wall[0] as string).startsWith("door"))
              quantity += this.getSlidingWallPanels(wall[0] as string);
          });
          total = item.price * quantity;
        }
        if (item.name === "windowdoors") {
          truewalls.map((wall) => {
            if ((wall[0] as string).startsWith("window"))
              quantity += this.getSlidingWallPanels(wall[0] as string);
          });
          total = item.price * quantity;
        }
        if (item.name === "windowdoorsdark") {
          truewalls.map((wall) => {
            if (wall[2] && (wall[0] as string).startsWith("window"))
              quantity += this.getSlidingWallPanels(wall[0] as string);
          });
          total = item.price * quantity;
        }
        if (
          item.name === "aluminiumWall" ||
          item.name === "aluminiumWallPolySpie" ||
          item.name === "aluminiumWallStaandePoly"
        ) {
          truewalls.map((wall) => {
            if (wall[0] === item.name) {
              quantity += this.getAluminiumWallLength(wall[1] as number);
            }
          });

          total = item.price * quantity;
        }

        if (item.name === "fundering") {
          if (this.state?.optionsMaterial.fundering === "fundatiekoker")
            truewalls.map((item) => {
              if (item[0].startsWith("door") || item[0].startsWith("window"))
                quantity += this.getFunderingStockLength(item[1]);
            });
          total = item.price * quantity;
        }
      }
      if (true) {
        const slidingGlassWalls = (this.walls ?? []).filter(
          (w) =>
            w.woodWall.outerColor.startsWith("door") ||
            w.woodWall.outerColor.startsWith("window")
        );

        if (item.name === "doorBrushes") {
          quantity = slidingGlassWalls.filter(
            (w) => w.slidingGlassWallOptions.doorBrushes
          ).length;
          total = quantity * item.price;
        }
        if (item.name === "stainlessSteelHandle") {
          quantity = slidingGlassWalls.filter(
            (w) => w.slidingGlassWallOptions.stainlessSteelHandle
          ).length;
          total = quantity * item.price;
        }
        if (item.name === "lockProfile") {
          quantity = slidingGlassWalls.filter(
            (w) => w.slidingGlassWallOptions.lockProfile
          ).length;
          total = quantity * item.price;
        }
        if (item.name === "schuifrichting") {
          quantity = slidingGlassWalls.filter(
            (w) => w.slidingGlassWallOptions.schuifrichting !== "empty"
          ).length;
          total = quantity * item.price;
        }
        if (item.name.startsWith("glasbok_")) {
          const glassColor = item.name.replace("glasbok_", "");
          quantity = slidingGlassWalls.reduce(
            (acc, w) =>
              w.slidingGlassWallOptions.glasbok > 0 &&
                w.slidingGlassWallOptions.glassColor === glassColor
                ? acc + w.slidingGlassWallOptions.glasbok
                : acc,
            0
          );
          total = quantity * item.price;
        }
      }

      if (lights) {
        const everyLigger = this.state?.lights.everyLigger;

        const amount =
          this.getTablePanelCount(
            houseWidth,
            glassCheck,
            oase ? "oase" : "aludra"
          ) - 1;

        if (item.name === "spotlight") {
          quantity = everyLigger ? amount * 2 : amount - (amount % 2);
          total = quantity * item.price;
        }
      }

      if (true) {
        if (item.name === "unevenwidth") {
          quantity = houseWidth % 1 === 0 ? 0 : 1;
          total = item.price;
        }
        if (item.name === "unevendepth") {
          quantity = houseDepth % 1 === 0 ? 0 : tableLiggerCount;
          total = quantity * item.price;
        }
      }

      if (true) {
        if (item.name === "poer") {
          quantity = poer ? columns : 0;
          total = quantity * item.price;
        }
        if (item.name === "hwapoer") {
          quantity = hwapoer ? columns : 0;
          total = quantity * item.price;
        }
      }

      return {
        ...item,
        quantity,
        total,
      };
    });

    const opslagItem = this.data.find(
      (row) => row?.name === "opslagGlasplaatDak"
    );
    if (opslagItem) {
      opslagItem.price = this.getOpslagGlasplaatDakPercent();
      opslagItem.quantity = this.getGlasplaatDakSubtotal() > 0 ? 1 : 0;
      opslagItem.total = this.getOpslagGlasplaatDakTotal();
    }

    const canopyRow = this.getCanopyRow();
    if (canopyRow) {
      this.data.unshift(canopyRow);
    }

    this.getCanopyExtras().forEach((extra) => {
      this.data.push({
        name: extra.article,
        label: `${extra.label} ${extra.article} (${formatCanopySizeInCm(
          extra.width,
          extra.depth
        )})`,
        unit: "stuk",
        quantity: 1,
        price: 0,
        total: 0,
      });
    });

    const staanderMeerprijsQuantity =
      this.state.roofType === "oase"
        ? getStaanderMeerprijsQuantity(height, this.getStaanderCount())
        : 0;

    this.data.push({
      name: "staanderMeerprijs",
      label: "Staander meerprijs",
      unit: "stuk",
      price: STAANDER_MEERPRIJS,
      quantity: staanderMeerprijsQuantity,
      total: staanderMeerprijsQuantity * STAANDER_MEERPRIJS,
    });

    const montageItem = this.data.find((row) => row?.name === "montage");
    if (montageItem) {
      const montageChosen = this.state.optionsMaterial.montage;
      montageItem.price = this.getMontagePercent();
      montageItem.quantity = montageChosen ? 1 : 0;
      montageItem.total = montageChosen ? this.getMontageTotal() : 0;
    }

    return this.data;
  }
}

export default CalculationService;
